<template>
  <div class="base-input-v2">
    <label
      class="body-smaller-semi-bold"
      :for="id">{{ labelText }}</label>
    <div class="base-input-v2__container">
      <div v-if="prefix" class="base-input-v2__prefix">
        <span :id="`${id}-prefix`" class="sr-only">{{ tStatic('fieldPrefixedAria').replaceAll('{0}', prefix) }}</span>
        {{ prefix }}
      </div>
      <div class="base-input-v2__wrapper">
        <div v-if="$slots.iconPrepend" class="base-input-v2__icon-prepend">
          <slot name="iconPrepend"></slot>
        </div>
        <input
          v-if="mask"
          v-model.trim="proxyModel"
          v-mask="mask"
          v-bind="mergedAttrs"
          class="base-input-v2__input"
          @blur="($event) => $emit('blur', $event)"
          @keyup="($event) => $emit('keyup', $event)"
          @keydown="($event) => $emit('keydown', $event)">
        <input
          v-else
          v-model.trim="proxyModel"
          v-bind="mergedAttrs"
          class="base-input-v2__input"
          @blur="($event) => $emit('blur', $event)"
          @keyup="($event) => $emit('keyup', $event)"
          @keydown="($event) => $emit('keydown', $event)">
        <div v-if="$slots.iconAppend" class="base-input-v2__icon-append">
          <slot name="iconAppend"></slot>
        </div>
      </div>
    </div>
    <div
      v-show="errors.length"
      :id="`${id}-error`"
      class="body-smaller base-input-v2__error-container">
      <div class="base-input-v2__error">
        <oie-base-icon icon="warning" size="xs"></oie-base-icon>
        <span v-for="error in errors" :key="error"> {{ error }}</span>
      </div>
    </div>
    <div
      v-show="description"
      :id="`${id}-description`"
      class="body-smaller"
      v-html="description">
    </div>
  </div>
</template>

<script>
import { mask } from 'vue-the-mask'
import _ from 'lodash'

export default {
  directives: { mask },
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number, Date],
      default: null
    },
    label: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Array,
      default: () => []
    },
    description: {
      type: String,
      default: ''
    },
    prefix: {
      type: String,
      default: ''
    },
    mask: {
      type: [String, Boolean, Object],
      default: null
    }
  },
  computed: {
    id() {
      return this.$attrs.id || _.uniqueId('base-input-v2-')
    },
    labelText() {
      return this.required ? `${this.label}*` : this.label
    },
    mergedAttrs() {
      return {
        id: this.id,
        'aria-required': this.required ? 'true' : 'false',
        'aria-invalid': this.errors.length ? 'true' : 'false',
        'aria-describedby': `${this.id}-error ${this.id}-prefix ${this.id}-description`,
        ...this.$attrs
      }
    },
    proxyModel: {
      get() {
        function escapeRegExp(str) {
          return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // escape special characters
        }
        return (this.prefix && this.value) ? this.value.replace(new RegExp('^' + escapeRegExp(this.prefix)), '') : this.value
      },
      set(value) {
        (this.prefix && value) ? this.$emit('input', this.prefix + value) : this.$emit('input', value)
      }
    }
  }
}
</script>

<style lang="scss">
// BASE
.base-input-v2 {
  display: flex;
  flex-direction: column;
  gap: measure(quarter);
}

.base-input-v2__container {
  display: flex;
  align-items: stretch;
  gap: measure(quarter);
}

.base-input-v2__prefix {
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: color(background-gray-5);
  border: 1px solid color(border-gray);
  color: color(text-gray);
  padding: 0 measure(half);
}

.base-input-v2__wrapper {
  position: relative;
  flex: 1 1 auto;
}

.base-input-v2__input {
  width: 100%;
  height: 3.125rem;
  padding: 0 measure(half);
  border: 1px solid color(border-gray);
}

// ICON STYLES
.base-input-v2__wrapper:has(.base-input-v2__icon-prepend) .base-input-v2__input{
  padding-left: 40px;
}

.base-input-v2__wrapper:has(.base-input-v2__icon-append) .base-input-v2__input{
  padding-right: 40px;
}

[class^="base-input-v2__icon"] {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.base-input-v2__icon-prepend {
  left: 13px;
}

.base-input-v2__icon-append {
  right: 13px;
}

// ERROR STYLES
.base-input-v2__input[aria-invalid="true"] {
  border-color: color(text-red);
}

.base-input-v2__error-container {
  display: flex;
  flex-direction: column;
  gap: measure(eighth);
  color: color(text-red);
}

.base-input-v2__error {
  line-height: 1;
  display: flex;
  gap: measure(eighth);
  align-items: center;
}

// READONLY STYLES
.base-input-v2__input[readonly] {
  background-color: color(background-gray-5);
  border: 1px solid color(border-gray);
  color: color(text-gray);
}
</style>
